import { useQuery } from '@tanstack/react-query';
import { adminViews, isForbidden, schedule } from '@cwos/api-client';
import { Pill, Refused, SkeletonSet } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { ADMIN_VIEWS_READ, usePermissions } from '../permissions.js';
import { authorityGate, gateOpen } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { coverageGaps } from '../schedule/scheduleLogic.js';
import { LoadFailure } from '../LoadFailure.js';
import {
  boardScopeNote,
  durationLabel,
  todayWindow,
  worstMissingClockOut,
} from './workforceNowLogic.js';

/**
 * HAPPENING NOW — the live-operations half of the Right Now command surface
 * (Phase B; owner directive: "what is happening now, who is working, what is
 * currently wrong" — action-first, not KPI-first).
 *
 * Two reads, both already on the server and both polled gently:
 *
 *   `GET /api/admin-views/workforce` — who is on the clock, missing
 *   clock-outs, unscheduled no-shows. Gated `admin-views:read`, the key the
 *   board's own routes charge (gate-keys.test.ts holds that binding).
 *
 *   `GET /schedule/shifts` over TODAY — planned coverage and the shifts that
 *   started with no session (`coverageGaps`, whose rules — cancelled is not a
 *   gap, the future is not a gap — this panel inherits rather than restates).
 *   Read-only: the auto-match sweep is the Scheduling screen's deliberate
 *   write and has no place on a landing screen.
 *
 * A principal REFUSED both keys gets no panel at all — same rule as the rail:
 * absent, never a ghost, never a guaranteed 403. A principal whose permission
 * set has not arrived, or could not be read, is not that principal and no
 * longer shares the answer: see the gate below. A held key whose read then
 * refuses (a program-scoped grant) renders the refusal as a refusal.
 * Every count is four-state: loading is not zero, failed is not zero, and
 * the board's caseload frame is said under its own figures.
 */
export function WorkforceNow() {
  const navigate = useNav();
  const permissions = usePermissions();
  /**
   * FOUR STATES, BECAUSE THE PANEL'S ONLY OTHER ANSWER IS TO DISAPPEAR.
   *
   * `has()` is false for "not held", "not arrived" and "could not be read"
   * alike. Read that way, `!canReadBoard && !canReadSchedule` was true on the
   * FIRST PAINT of every load and stayed true forever if `/auth/me` failed —
   * and the consequence is `return null`, which on a route that gates nothing
   * is a panel that silently ceases to exist. It is the safe direction for a
   * BUTTON, where nothing false is said; it is not the safe direction for a
   * whole section of a landing screen, because absence is itself a claim here
   * ("there is nothing happening for you to see") and no retry is offered for
   * a failure the reader is never told about.
   *
   * So: both gates REFUSED still removes the panel, exactly as before and for
   * the reason the docblock above gives. Anything less certain says which of
   * the other three it is.
   */
  const boardGate = authorityGate(permissions, ADMIN_VIEWS_READ);
  const scheduleGate = authorityGate(permissions, 'schedule:read');
  const canReadBoard = gateOpen(boardGate);
  const canReadSchedule = gateOpen(scheduleGate);

  const board = useQuery({
    queryKey: ['admin-views', 'workforce', 'right-now'],
    queryFn: () => adminViews.workforce(),
    refetchInterval: 30_000,
    enabled: canReadBoard,
  });
  const roster = useQuery({
    queryKey: ['schedule', 'shifts', 'today'],
    queryFn: () => schedule.coverageRoster(todayWindow(new Date())),
    refetchInterval: 60_000,
    enabled: canReadSchedule,
  });

  if (!canReadBoard && !canReadSchedule) {
    // `unresolved` before `unknown`: a set still in flight is the commoner of
    // the two and is not a failure anybody should be offered a retry for.
    const pending =
      [boardGate, scheduleGate].find((g) => g.kind === 'unresolved') ??
      [boardGate, scheduleGate].find((g) => g.kind === 'unknown');
    if (pending === undefined) return null;
    return (
      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">Happening now</h2>
        </div>
        <GateNotice
          gate={pending}
          what="see what is happening now"
          onRetry={() => permissions.refetch()}
          // `refused` is answered ABOVE by removing the panel, so it cannot
          // reach this component — `pending` is `unresolved | unknown` by
          // construction. Rendering nothing rather than a heading keeps that
          // true if the branch above ever changes: an unreachable refusal
          // sentence is a sentence nobody has read since it was written.
          refusedNode={null}
        />
      </section>
    );
  }

  const gaps = roster.data ? coverageGaps(roster.data.shifts, new Date()) : null;
  /**
   * Does this organization plan SHIFTS? The board says so when it has been read;
   * without it (no `admin-views:read`, or still loading) today's roster stands
   * in — an empty one is not shown, because "0 shifts today" is a false zero
   * for an organization whose planned work is visits.
   */
  const hasShifts =
    board.data?.hasShiftSchedule ?? (roster.data ? roster.data.shifts.length > 0 : undefined);
  /** "Started with no session" measures the time clock; with none, it is not measured. */
  const noClock = board.data?.hasTimeClock === false;
  const scopeNote = board.data ? boardScopeNote(board.data.scope) : null;
  const worstOpen = board.data ? worstMissingClockOut(board.data.missingClockOuts) : null;

  return (
    <section className="rn-card">
      <div className="rn-card-head">
        <h2 className="rn-card-title">Happening now</h2>
        {canReadBoard ? (
          <button type="button" className="rn-link" onClick={() => navigate('/time')}>
            Time &amp; Timesheets →
          </button>
        ) : null}
      </div>

      {canReadBoard ? (
        board.isLoading ? (
          <SkeletonSet surface="table" rows={2} />
        ) : board.isError && !board.data ? (
          isForbidden(board.error) ? (
            <Refused
              heading="The live board is not shown to you"
              message="Your account's workforce-board access does not reach this read. Nothing here says nobody is working."
            />
          ) : (
            <LoadFailure
              error={board.error}
              message="We couldn't read who is on the clock just now. Nothing here says nobody is working."
              onRetry={() => void board.refetch()}
            />
          )
        ) : board.data?.hasTimeClock === false ? (
          // NO TIME CLOCK, NO CLOCK FIGURES. "0 on the clock" and "N scheduled
          // and not clocked in" would be measurements of a source this
          // organization never connected (owner ruling 2026-10-07).
          <p className="rn-muted">
            No time clock connected — this organization does not record clock-ins, so nobody is
            shown as on or off the clock.
          </p>
        ) : board.data ? (
          <div className="now-board">
            <div className="now-line">
              <span className="now-count">{board.data.currentlyClockedIn}</span>
              <span className="rn-muted">on the clock</span>
              {scopeNote ? <span className="rn-muted"> · {scopeNote}</span> : null}
            </div>
            {board.data.activeSessions.slice(0, 4).map((s) => (
              <button
                key={s.sessionId}
                type="button"
                className="now-row"
                onClick={() => navigate('/time')}
              >
                <span className="now-row-name">{s.displayName}</span>
                <span className="rn-muted">{durationLabel(s.durationMinutes)}</span>
                {s.locationStatus === 'verified' ? <Pill variant="sky">verified</Pill> : null}
              </button>
            ))}
            {board.data.missingClockOuts.length > 0 ? (
              <button type="button" className="now-alarm" onClick={() => navigate('/time')}>
                <Pill variant="look" dot>
                  {`${board.data.missingClockOuts.length} missing clock-out${
                    board.data.missingClockOuts.length === 1 ? '' : 's'
                  }`}
                </Pill>
                {worstOpen ? (
                  <span className="rn-muted">
                    {`${worstOpen.displayName} · open ${durationLabel(worstOpen.openDurationMinutes)}`}
                  </span>
                ) : null}
              </button>
            ) : null}
            {board.data.unscheduledNoShows.length > 0 ? (
              <button type="button" className="now-alarm" onClick={() => navigate('/time')}>
                <Pill variant="look" dot>
                  {`${board.data.unscheduledNoShows.length} scheduled and not clocked in`}
                </Pill>
              </button>
            ) : null}
          </div>
        ) : null
      ) : null}

      {/* A SHIFT roster is not a measurement for an organization whose planned
          work is visits: "0 shifts today" there is a false zero. */}
      {canReadSchedule && hasShifts !== false ? (
        roster.isLoading ? (
          <SkeletonSet surface="table" rows={1} />
        ) : roster.isError && !roster.data ? (
          <p className="rn-muted">
            Today's coverage couldn't be read just now — nothing here says the day is covered.{' '}
            <button type="button" className="rn-link" onClick={() => void roster.refetch()}>
              Try again
            </button>
          </p>
        ) : roster.data && gaps ? (
          <button type="button" className="now-coverage" onClick={() => navigate('/scheduling')}>
            <span className="rn-muted">
              {`${roster.data.shifts.length} shift${
                roster.data.shifts.length === 1 ? '' : 's'
              } today`}
              {roster.data.scope === 'own' ? ' (yours)' : ''}
            </span>
            {noClock ? null : gaps.length > 0 ? (
              <Pill variant="look" dot>
                {`${gaps.length} started with no session`}
              </Pill>
            ) : (
              <span className="rn-muted">· every started shift has a session</span>
            )}
            <span className="rn-link">Schedule →</span>
          </button>
        ) : null
      ) : null}
    </section>
  );
}
